<template>
	<div class="lineup-detail">
		<div class="back-button">
			<div @click="backToLineup">
				<Icon
					icon-name="arrow-left"
					size="sm"
				/>
				<span>{{ $I18n.trans('commons.back_to_lineup') }}</span>
			</div>
		</div>
		<div class="player">
			<div class="player-header">
				<div
					v-if="participantImage"
					class="player-image"
					:style="{
						background: `var(--color-grey-950) url(${participantImage}) no-repeat center center / cover`,
					}"
				>
					<div class="player-image-overlay" />
					<div class="player-number">
						<p>{{ participantNumber }}</p>
					</div>
				</div>
				<div
					v-else
					class="player-image-fallback"
				>
					<Icon
						icon-name="single"
						size="xl"
					/>
					<div class="player-image-overlay" />
					<div class="player-number">
						<p>{{ participantNumber }}</p>
					</div>
				</div>
				<div
					class="player-info"
					:class="{'is-mobile': isMobileWebApp}"
				>
					<div class="player-name">
						<p class="text-ellipsis last-name">
							{{ participantFirstName }}
						</p>
						<p class="text-ellipsis first-name">
							{{ participantLastName }}
						</p>
					</div>
				</div>
			</div>
			<player-data
				:details-labels="detailsLabels"
				:participant="participant"
			/>
		</div>

		<div
			v-if="participantStats && participantStats.body.length"
			class="player-stats-wrapper"
		>
			<div class="menu">
				<div class="title">
					{{ $I18n.trans('view_mode.match_statistics') }}
				</div>
			</div>
			<div
				class="player-stats"
			>
				<div
					v-for="(column, idx) in participantStats.header"
					:key="idx"
					class="player-stats-column"
				>
					<div class="stats-key">
						{{ column }}
					</div>
					<div class="stats-value">
						{{ participantStats.body[idx] }}
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import PlayerData from './PlayerData.vue';
import { mapGetters } from 'vuex';

export default {
	name:       'LineupDetail',
	components: { PlayerData },
	props:      {
		participant: {
			type:    Object,
			default: () => {},
		},
		detailsLabels: {
			type:    Object,
			default: () => ({
				nationality: $I18n.trans('commons.nationality'),
				born:        $I18n.trans('commons.born'),
				position:    $I18n.trans('commons.position'),
			}),
		},
		participantStats: {
			type:    Object,
			default: () => {},
		},
	},
	computed: {
		...mapGetters({
			isMobileWebApp: 'app/isMobileWebApp',
		}),
		participantFirstName() {
			if (!this.participant) {
				return null;
			}
			return this.participant.firstName;
		},
		participantLastName() {
			if (!this.participant) {
				return null;
			}
			return this.participant.lastName;
		},
		participantImage() {
			if (!this.participant) {
				return null;
			}

			// Do not show the asport placeholder image
			if (this.participant.imageUrl?.includes('placeholder')) {
				return null;
			}

			return this.participant.imageUrl;
		},
		participantNumber() {
			if (!this.participant) {
				return null;
			}
			return this.participant.number;
		},
	},
	methods: {
		backToLineup() {
			this.$emit('back-to-line-up', null);
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@shared/sass/shared-variables';
	@import '@videotools/sass/_fonts';

	.lineup-detail {
		color: var(--color-grey-100);
		display: flex;
		flex-direction: column;
		height: 100%;
		padding-left: 8px;

		.spinner {
			height: 100%;
		}
		.text-ellipsis {
			white-space: nowrap;
			overflow: hidden;
			text-overflow: ellipsis;
		}

		.back-button {
			@include text-sm-2;
			color: var(--color-grey-500);
			display: flex;
			align-items: center;
			padding-top: 16px;
			padding-bottom: 16px;
			border-bottom: 1px solid var(--color-grey-900);

			div {
				cursor: pointer;
				display: flex;
				align-items: center;

				&:hover {
					color: var(--color-soft-white);

					body.-t-light & {
						color: var(--color-black);
					}
				}
			}

			span {
				margin-left: 19px;
			}
		}

		.player-stats-column {
			display: flex;
			flex-direction: column;
		}

		.player-header {
			display: flex;
			width: 100%;
		}

		.player {
			display: flex;
			align-items: flex-end;
			flex-wrap: wrap;
			margin-top: 26px;
		}

		.player-image {
			border-radius: 50%;
			width: 80px;
			height: 80px;
			position: relative;
			border: 1px solid var(--color-grey-850);
		}

		.player-image-fallback {
			border-radius: 50%;
			width: 80px;
			height: 80px;
			border: 1px solid var(--color-grey-850);
			display: flex;
			justify-content: center;
			align-items: center;
			position: relative;

			::v-deep svg {
				color: var(--color-grey-400);
			}
		}

		.last-name {
			@include text-md-2;
			font-weight: 500;
		}

		.first-name {
			@include text-lg-1(true);
			font-size: 28px;
		}

		.player-number {
			position: absolute;
			border: 1px solid var(--color-soft-white);
			border-radius: 50%;
			bottom: 0;
			background: var(--color-black);
			right: -10px;
			width: 32px;
			height: 32px;
			display: flex;
			align-items: center;
			justify-content: center;

			body.-t-light & {
				background: var(--color-white);
			}
		}

		.player-number p {
			@include text-md-1(true);
			text-align: center;
		}

		.player-info {
			width: 0; // for text overflow
			flex-grow: 1;
			margin-top: 18px;
			margin-left: 20px;

			&.is-mobile {
				margin-top: 0;
			}
		}

		.last-name {
			@include text-md-1;
		}
		.first-name {
			line-height: 26px;
			font-size: 20px;
			font-weight: 500;
		}

		.player-stats-wrapper {
			@include text-sm-2;
			display: flex;
			flex-direction: column;
			background: var(--color-grey-950);
			margin: 32px 0 0 0;

			.menu {
				display: flex;
				justify-content: center;
				margin-bottom: 24px;
			}

			.title {
				text-align: center;
				color: var(--color-soft-white);
				margin-top: 16px;
			}

			.title::after {
				margin-top: 16px;
				content: " ";
				display: block;
				height: 1px;
				border-bottom: 1px solid var(--color-hype-yellow);
				width: 160px;
			}

		}

		.player-stats {
			width: 100%;
			color: var(--color-grey-100);
			border-collapse: separate;
			border-spacing: 0;
			border: 0;
			table-layout: fixed;
			display: flex;
			justify-content: space-between;
			padding-right: 20px;
			padding-left: 20px;

			.stats-key {
				border: 0;

				@include text-sm-2;
				border: 0;
				text-align: center;
				padding-top: 12px;
				color: var(--color-grey-300);
				white-space: nowrap;
				overflow: hidden;
				text-overflow: ellipsis;
			}

			.stats-value {
				border: 0;

				@include text-lg-1;
				border: 0;
				color: var(--color-soft-white);
				font-weight: 500;
				text-align: center;
				padding-bottom: 24px;
			}
		}
	}
</style>
